import { useMemo, useState } from "react";
import { ChevronDown } from "@/components/animate-ui/icons/chevron-down";
import { ChevronLeft } from "@/components/animate-ui/icons/chevron-left";
import { ChevronRight } from "@/components/animate-ui/icons/chevron-right";
import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getLogoUrl } from "@/constants/integration-logos";
import {
	CATEGORIES,
	type TaskCategory,
	TEMPLATE_INTEGRATIONS,
	TEMPLATES,
	TEMPLATES_PER_PAGE,
} from "@/constants/task-templates";
import { cn } from "@/lib/utils";
import { shuffleArray } from "@/lib/utils/shuffle";
import {
	filterTemplates,
	getNextIntegrationsState,
	getTotalPages,
	paginateTemplates,
} from "@/lib/utils/task-form";

interface TemplateGalleryProps {
	onTemplateClick: (template: (typeof TEMPLATES)[0]) => void;
}

/**
 * Gallery of task templates with category and integration filters.
 *
 * Displays paginated task templates with filtering by category and integration type.
 * Shows a divider with "or start with a template" text above the gallery.
 * Templates are shuffled on mount so order varies per page load.
 */
export function TemplateGallery({ onTemplateClick }: TemplateGalleryProps) {
	const [shuffledTemplates] = useState(() => shuffleArray(TEMPLATES));
	const [selectedCategory, setSelectedCategory] = useState<TaskCategory>("All");
	const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([]);
	const [integrationSearch, setIntegrationSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);

	const handleToggleIntegration = (integrationId: string) => {
		setSelectedIntegrations((prev) => getNextIntegrationsState(prev, integrationId));
		setCurrentPage(1);
	};

	// Filter and paginate templates
	const filteredTemplates = useMemo(
		() => filterTemplates(shuffledTemplates, selectedCategory, selectedIntegrations),
		[shuffledTemplates, selectedCategory, selectedIntegrations]
	);

	const visibleIntegrations = useMemo(
		() =>
			integrationSearch
				? TEMPLATE_INTEGRATIONS.filter((i) =>
						i.name.toLowerCase().includes(integrationSearch.toLowerCase())
					)
				: TEMPLATE_INTEGRATIONS,
		[integrationSearch]
	);

	const totalPages = getTotalPages(filteredTemplates.length, TEMPLATES_PER_PAGE);
	const paginatedTemplates = paginateTemplates(filteredTemplates, currentPage, TEMPLATES_PER_PAGE);

	const integrationsLabel =
		selectedIntegrations.length === 0
			? ""
			: selectedIntegrations.length === 1
				? (TEMPLATE_INTEGRATIONS.find((i) => i.id === selectedIntegrations[0])?.name ??
					"Integrations")
				: `${selectedIntegrations.length} integrations`;

	return (
		<>
			{/* Divider */}
			<div className="relative py-4 sm:py-5">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-t border-border" />
				</div>
				<div className="relative flex justify-center text-sm">
					<span className="bg-background px-4 text-muted-foreground">or start with an example</span>
				</div>
			</div>

			{/* Filters */}
			<div className="surface-glass surface-divider rounded-xl border p-3 sm:p-4">
				<div className="flex flex-wrap items-center justify-between gap-3">
					{/* Category Buttons */}
					<div className="flex w-full items-center gap-2 overflow-x-auto pb-1 sm:w-auto sm:flex-wrap sm:overflow-visible sm:pb-0">
						{CATEGORIES.map((category) => (
							<Button
								key={category}
								variant={selectedCategory === category ? "default" : "outline"}
								size="sm"
								onClick={() => {
									setSelectedCategory(category);
									setCurrentPage(1);
								}}
								className="h-8 shrink-0 text-xs sm:text-sm"
							>
								{category}
							</Button>
						))}
					</div>

					{/* Integration Filter Dropdown */}
					<DropdownMenu onOpenChange={(open) => !open && setIntegrationSearch("")}>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="h-8 w-full justify-between sm:w-auto sm:justify-center"
							>
								<PlugZap animateOnHover className="mr-1.5 size-4" />
								{integrationsLabel}
								<ChevronDown animateOnHover className="ml-1.5 size-4 opacity-50" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="max-h-72 w-56 overflow-y-auto">
							<div className="sticky top-0 z-10 bg-popover p-2">
								<input
									type="text"
									placeholder="Search apps..."
									value={integrationSearch}
									onChange={(e) => setIntegrationSearch(e.target.value)}
									onKeyDown={(e) => e.stopPropagation()}
									className="w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
								/>
							</div>
							<DropdownMenuSeparator />
							{visibleIntegrations.length === 0 ? (
								<p className="px-2 py-1.5 text-sm text-muted-foreground">No apps found</p>
							) : (
								visibleIntegrations.map((integration) => (
									<DropdownMenuCheckboxItem
										key={integration.id}
										checked={selectedIntegrations.includes(integration.id)}
										onCheckedChange={() => handleToggleIntegration(integration.id)}
									>
										<img
											src={getLogoUrl(integration.logoKey)}
											alt={`${integration.name} logo`}
											className="mr-2 size-4 object-contain"
										/>
										{integration.name}
									</DropdownMenuCheckboxItem>
								))
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Template Cards */}
			{filteredTemplates.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<p className="text-muted-foreground">No templates match your filters.</p>
					<Button
						variant="link"
						size="sm"
						className="mt-2"
						onClick={() => {
							setSelectedCategory("All");
							setSelectedIntegrations([]);
							setCurrentPage(1);
						}}
					>
						Clear filters
					</Button>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-3">
					{paginatedTemplates.map((template) => {
						const primary = template.integrations[0];
						return (
							<Card
								key={template.id}
								role="button"
								tabIndex={0}
								className="group cursor-pointer border-border/70 bg-card/95 transition-[border-color,box-shadow,background-color] duration-200 hover:border-accent-blue-border hover:shadow-md focus-visible:border-accent-blue-border focus-visible:ring-2 focus-visible:ring-ring/40"
								onClick={() => onTemplateClick(template)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										onTemplateClick(template);
									}
								}}
							>
								<CardHeader className="space-y-3 px-5 py-5">
									<div className="flex items-center gap-3">
										{primary ? (
											<img
												src={getLogoUrl(primary.logoKey)}
												alt={`${primary.name} logo`}
												className="size-6 object-contain"
											/>
										) : (
											<PlugZap className="size-6 text-primary" />
										)}
										<CardTitle className="text-lg">{template.title}</CardTitle>
									</div>
									<CardDescription className="text-sm leading-relaxed">
										{template.description}
									</CardDescription>
								</CardHeader>
							</Card>
						);
					})}
				</div>
			)}

			{/* Pagination Controls - Minimalistic */}
			{filteredTemplates.length > TEMPLATES_PER_PAGE && (
				<div className="flex items-center justify-center gap-3 pt-5">
					<Button
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
						disabled={currentPage === 1}
					>
						<ChevronLeft animateOnHover className="size-4" />
					</Button>
					<div className="flex items-center gap-1.5">
						{(() => {
							const maxDots = Math.min(5, totalPages);
							const half = Math.floor(maxDots / 2);
							const end = Math.min(totalPages, Math.max(maxDots, currentPage + half));
							const start = Math.max(1, end - maxDots + 1);
							return Array.from({ length: maxDots }, (_, i) => {
								const page = start + i;
								return (
									<button
										key={`page-${page}`}
										type="button"
										onClick={() => setCurrentPage(page)}
										className={cn(
											"size-2.5 rounded-full transition-colors",
											currentPage === page
												? "bg-primary shadow-sm"
												: "bg-muted-foreground/30 hover:bg-muted-foreground/50"
										)}
									/>
								);
							});
						})()}
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
						disabled={currentPage === totalPages}
					>
						<ChevronRight animateOnHover className="size-4" />
					</Button>
				</div>
			)}
		</>
	);
}
